import { isAxiosError } from 'axios';
import ArcGisError from '@arcgis/core/core/Error.js';
import { _ } from '../translator';

let release = null;

if (window.SENTRY_RELEASE) {
    release = window.SENTRY_RELEASE.id;
}
function addAxiosContext(event, error) {
    if (error.response) {
        const contexts = { ...event.contexts };
        contexts.Axios = { Response: error.response };
        event.contexts = contexts;
        event.transaction = `Axios ${error.response.status}: ${error.response.config.url}`;
    }
}
function addAxiosContextRecursive(event, error) {
    if (isAxiosError(error)) {
        addAxiosContext(event, error);
    } else if (error instanceof Error && error.cause) {
        addAxiosContextRecursive(event, error.cause);
    }
}
function wrapArcGISError(error) {
    const wrapped = new Error(
        `[${error.name || 'ArcGISError'}] ${error.message}`
    );

    // Copy all ArcGIS properties dynamically for context
    Object.keys(error).forEach((key) => {
        wrapped[`arcgis_${key}`] = error[key];
    });

    // Preserve cause if present
    if (error.cause) wrapped.cause = error.cause;
    // Preserve name if present
    if (error.name) wrapped.name = error.name;
    return wrapped;
}

function addArcGISContext(event, error) {
    const contexts = { ...event.contexts };
    const arcgisContext = {};
    Object.keys(error).forEach((key) => {
        if (key.startsWith('arcgis_')) {
            arcgisContext[key.replace('arcgis_', '')] = error[key];
        }
    });
    contexts.ArcGIS = arcgisContext;
    event.contexts = contexts;
}

function addArcGisContextRecursive(event, error) {
    if (error instanceof ArcGisError) {
        const wrappedError = wrapArcGISError(error);

        // Replace original exception in hint so Sentry picks up stack automatically
        event.originalException = wrappedError;
        event.transaction = `ArcGIS ${error.details.messageCode}: ${error.details.url}`;
        addArcGISContext(event, wrappedError);
    } else if (error instanceof Error && error.cause) {
        addArcGisContextRecursive(event, error.cause);
    }
}

function handleSentryBeforeSend(event, hint) {
    addAxiosContextRecursive(event, hint?.originalException);
    addArcGisContextRecursive(event, hint?.originalException);
    return event; // Must return event for Sentry v10+
}

const SENTRY_CONFIG = {
    allowUrls: [/(?:www)?\.?hemswx-?(?:dev|stage)?\.(?:no|se|dk)/],
    beforeSend: handleSentryBeforeSend,
    denyUrls: [
        // Google Adsense
        /pagead\/js/i,
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    dialogOptions: {
        errorFormEntry: _(
            'Noen felter har feilaktig data. Korriger dem gjerne og prøv igjen'
        ),
        errorGeneric: _(
            'Noe gikk galt ved insendning av din rapport, prøv igjen senere'
        ),
        labelClose: _('Lukk'),
        labelComments: _('Kommentar'),
        labelEmail: _('Epost'),
        labelName: _('Navn'),
        labelSubmit: _('Send'),
        subtitle: _('Support har blitt varlset.'),
        subtitle2: _(
            'Vi setter stort pris på hvis du tar dig tid til å fortelle oss som skjedde, i skjemaet nedan.'
        ),
        successMessage: _('Takk for din feedback!'),
        title: _('Ojda, vi verkar har problemer.')
    },
    dsn: 'https://7d86cdb346544f918051feb3754edf88@o1109013.ingest.sentry.io/6272709',
    // eslint-disable-next-line compat/compat
    environment: window.location.hostname,
    ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.',
        // Avast extension error
        '_avast_submit',
        /DataCloneError/,
        'Request canceled'
    ],
    // if your build tool supports it.
    integrations: [],
    normalizeDepth: 10,
    release,
    showDialog: true,
    // We recommend adjusting this value in production
    tracesSampleRate: 0.25,
    tunnel: '/tunnel.php'
};
export default SENTRY_CONFIG;
